import { useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useCustomNavigate } from 'core/hooks/useCustomNavigate'
import { route } from "core/router";
import {
  requestRecipeSuggestionRequest,
  cancelSearchRecipe,
  fetchRecipeSuggestionRequest,
  reloadRecipeSuggestionRequest,
  fetchRecipeDetailRequest,
  fetchRecipeDetailSuccess,
  requestRecipeSuggestionSuccess,
  resetRecipe,
  updateFavorite,
  searchRecipeRequest,
  resetSearchRecipe,
  searchAiRecipeRequest,
} from "./../store/actions";
import {
  IRecipeDetail,
} from "./../store/types";
import { useAppDispatch } from "core/hooks/useAppDispatch";
import { useReadNotification } from "modules/Menu/hooks/notification";

export const useResetRecipe = () => {
  const dispatch = useDispatch()

  const onReset = useCallback(() => {
    dispatch(resetRecipe());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetRecipe])

  return onReset
}

export const useRecipes = (
  ingredients: any[],
  options: any[],
  keyword?: string,
  isIgnoreIds?: boolean
) => {
  const dispatch = useDispatch()
  const appDispatch = useAppDispatch()
  const request = useSelector((state: any) => state.Recipes.request);
  const list = useSelector((state: any) => state.Recipes.list);
  const { params, data, loading } = list
  // get request from url
  const [searchParams] = useSearchParams();
  const readNotification = useReadNotification();

  useEffect(() => {
    if (searchParams.get("request")) {
      dispatch(requestRecipeSuggestionSuccess({
        id: Number(searchParams.get("request")),
      }));
    } else {
      appDispatch(requestRecipeSuggestionRequest({
        ingredient_ids: ingredients.map(e => e.id),
        request_ingredients: [],
        flavor_item_ids: options,
        keyword,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredients, options, searchParams.get("request")])

  useEffect(() => {
    // read notification if url has param notification
    if (searchParams.get("notification")) {
      readNotification([searchParams.get("notification")]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("notification")])

  useEffect(() => {
    return () => {
      dispatch(cancelSearchRecipe())
    }
  }, [])

  const fetchData = useCallback((page?: number) => {
    if (request?.data?.id) {
      appDispatch(fetchRecipeSuggestionRequest({
        page: page || params.page,
        page_size: params.page_size,
        id: request.data.id,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page_size, params.page, request])

  const fetchMoreData = useCallback((page?: number) => {
    if (request?.data?.id) {
      dispatch(fetchRecipeSuggestionRequest({
        page: page || params.page,
        page_size: params.page_size,
        id: request.data.id,
        ignore_ids: isIgnoreIds ? (data || []).map((e: IRecipeDetail) => e.id).join(',') : '',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page_size, params.page, request, data, isIgnoreIds])

  useEffect(() => {
    if (request?.data?.id) {
      fetchData(1)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.data?.id])

  const loadMore = useCallback(() => {
    if (loading) {
      return
    }
    fetchMoreData(params.page + 1)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, loading, fetchData])

  const reload = (cb: (state: boolean) => void, id?: number) => {
    dispatch(reloadRecipeSuggestionRequest({
      page: 1,
      page_size: data.length,
      id: request.data.id,
      callback: (data: any) => {
        return cb?.((data?.data || []).find((e: any) => e.id === id)?.is_favorite);
      }
    }));
  }

  return {
    loading,
    data,
    loadMore,
    params,
    reload,
    recipeRequestId: request?.data?.id || null,
    recipeRequestError: request?.error || null,
    searchRequestId: searchParams.get("request"),
  }
}

export const useRecipeDetail = (id?: string) => {
  const appDispatch = useAppDispatch()
  const dispatch = useDispatch()
  const detail = useSelector((state: any) => state.Recipes.detail);
  const request = useSelector((state: any) => state.Recipes.request);

  useEffect(() => {
    if (id) {
      appDispatch(fetchRecipeDetailRequest({
        id: Number(id),
        recipe_request_id: request?.data?.id || null
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const reload = (cb: any) => {
    dispatch(fetchRecipeDetailRequest({
      id: Number(id),
      recipe_request_id: request?.data?.id || null,
      callback: (data: IRecipeDetail) => cb?.(data.is_favorite),
    }));
  }

  return {
    ...detail,
    reload,
    recipeRequestId: request?.data?.id || null,
  }
}

export const useGoToRecipeDetail = (routerName: string) => {
  const dispatch = useDispatch()
  const navigate = useCustomNavigate()

  const goToDetail = useCallback((recipe?: IRecipeDetail) => {
    if (recipe?.id) {
      // set name, image, ...etc to detail
      if (recipe?.recipe_request_id) {
        dispatch(requestRecipeSuggestionSuccess({
          id: Number(recipe.recipe_request_id),
        }));
      }
      dispatch(fetchRecipeDetailSuccess(recipe));
      // navigate to detail
      navigate(route(routerName, recipe?.id))
    }
  }, [navigate, dispatch, routerName])

  return goToDetail
}

export const useUpdateFavorite = () => {
  const dispatch = useDispatch()

  const update = useCallback((id: number, is_favorite: boolean) => {
    if (id) {
      dispatch(updateFavorite({id, is_favorite}));
    }
  }, [dispatch])

  return update
}

export const useSearchRecipes = () => {
  const dispatch = useDispatch()
  const appDispatch = useAppDispatch()
  const list = useSelector((state: any) => state.Recipes.list);
  const listSearch = useSelector((state: any) => state.Recipes.listSearch);
  const request = useSelector((state: any) => state.Recipes.request);
  const { params, data, loading } = list
  const { type, loadingSearchAi, error: searchError, data: searchData } = listSearch

  const fetchData = useCallback((page?: number, paramSearch?:any) => {
    if(paramSearch?.keyword || params?.keyword) {
      appDispatch(searchRecipeRequest({
        page: page || params.page,
        page_size: params.page_size,
        keyword: paramSearch?.keyword || params.keyword,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page_size, params.page, params?.keyword])

  const fetchMoreData = useCallback((page?: number, dataKeyword?:string) => {
    dispatch(searchRecipeRequest({
      page: page || params.page,
      page_size: params.page_size,
      keyword: dataKeyword || params?.keyword,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page_size, params.page, params?.keyword])

  const loadMore = useCallback(() => {
    if (loading) {
      return
    }
    fetchMoreData(params.page + 1)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, loading, fetchData])

  const reload = (cb: (state: boolean) => void, dataKeyword?: string) => {
    dispatch(searchRecipeRequest({
      page: params.page,
      page_size: params.page_size,
      keyword: dataKeyword || params?.keyword,
    }));
  }

  const resetSearch = useCallback(() => {
    dispatch(resetSearchRecipe())
  }, [])

  const requestSearchAi = useCallback((dataSearch:any) => {
    dispatch(searchAiRecipeRequest(dataSearch))
  }, [])

  const requestRecipeSuggestionRequestByKeyword = useCallback((keyword: string) => {
    dispatch(requestRecipeSuggestionRequest({
      ingredient_ids: [],
      request_ingredients: [],
      flavor_item_ids: [],
      keyword,
    }))
  }, [])

  const cancelRequest = useCallback(() => {
    dispatch(cancelSearchRecipe())
  }, [])

  return {
    loading,
    data,
    type,
    loadMore,
    params,
    reload,
    searchError,
    searchData,
    requestRecipeSuggestionRequestByKeyword,
    cancelRequest,
    recipeRequestId: request?.data?.id || null,
    recipeRequestError: request?.error || null,
    recipeRequestLoading: request?.loading || false,
    fetchData,
    resetSearch,
    requestSearchAi,
    loadingSearchAi,
  }
}
